import { useState, useCallback } from "react";

const initialState = {};

function useForm({ formObj = initialState, validationRules = [] } = { formObj: initialState, validationRules: []}) {
  const [form, setForm] = useState(formObj);

  const renderFormInput = useCallback((name) => {
    const inputObj = { ...form[name] };
  
    return getFormInputProps(inputObj);
  },
    [form]
  )
  
  const renderFormInputs = useCallback(() => {
    return Object.values(form).map((inputObj) => {
      return getFormInputProps(inputObj);
    });
  },
   [form]
  )

  const getFormInputProps = useCallback((inputObj) => {
    const { name, value, errorMessage, valid, touched, isValidated } = inputObj;
    const defaultValue = form && form[name].defaultValue
      ? form[name].defaultValue
      : '';

    const fields = {
      name,
      value,
      valid,
      onChange: onInputChange,
      onValidate: onInputValidate,
      errorMessage: errorMessage,
      touched,
      defaultValue,
      isValidated
    };
    
    return fields;
  }, 
    [form]
  )

  const registerFormInput = useCallback((name) => {
    if (form && form[name]) {
      return renderFormInput(name);
    } else {
      // Initial values
      const fields = {
        name: name,
        value: '',
        valid: true,
        onChange: onInputChange,
        onValidate: onInputValidate,
        errorMessage: '',
        touched: false,
        isValidated: true,
      };

      setForm(prevState => ({
        ...prevState,
        [name]: {
          ...fields
        }
      }));
    
      return fields;
    }
  },
    [form]
  );

  const isInputFieldValid = useCallback(
    async (inputField, valueToCheck = null) => {
      const value = valueToCheck
        ? valueToCheck
        : inputField.value;

      for (const rule of validationRules) {
        const isValid = await rule.validate(value, form);

        if (!isValid) {
          inputField.errorMessage = rule.message;
          return false;
        }
      }

      return true;
    },
    [form, validationRules]
  );

  const onInputChange = useCallback(
    async (event) => {
      const { name, value } = event.target;
      const inputObj = { ...form[name] };

      inputObj.value = value;
      inputObj.touched = true;
      inputObj.isValidated = false;

      setForm({ ...form, [name]: inputObj });
    },
    [form]
  );

  const onInputValidate = useCallback(
    async (event, valueToCheck = null) => {
      const { name, value } = event.target;
      const newValue = valueToCheck
        ? valueToCheck
        : value;
      const inputObj = { 
        valid: form[name].valid,
        value: value,
        validationRules: form[name].validationRules,
      };
      const isValidInput = await isInputFieldValid(inputObj, newValue);

      setForm(prevState => ({
        ...prevState,
        [name]: {
          ...prevState[name],
          valid: isValidInput,
          isValidated: true,
          errorMessage: inputObj.errorMessage,
        }
      }));
    },
    [form, isInputFieldValid]
  );

  const setFormFieldValue = useCallback(
    (name, value) => {
      const inputObj = { ...form[name] };
      inputObj.value = value;

      if (!value) {
        inputObj.errorMessage = '';
        inputObj.valid = true;
      }

      setForm({ 
        ...form, 
        [name]: inputObj 
      });
    },
    [form]
  );

  const setDefaultValues = useCallback(
    (defaultValues) => {
      for (const key in defaultValues) {
          const defaultValue = defaultValues[key];

          setForm(prevState => ({
            ...prevState,
            [key]: {
              ...prevState[key],
              value: defaultValue,
              defaultValue: defaultValue,
            },
            // defaultValues: {
            //   ...prevState.defaultValues,
            //   [key]: defaultValue
            // }
          }));
      }
    },
    []
  );

  /**
   * returns boolean value indicating whether overall form is valid
   *
   * @param {object} formObj - object representation of a form
   */
  const isFormValid = useCallback(() => {
    let isValid = true;
    const arr = Object.values(form);

    for (let i = 0; i < arr.length; i++) {
      if (!arr[i].valid) {
        isValid = false;
        break;
      }
    }

    return isValid;
  }, [form]);
  
  const resetForm = useCallback(() => {
    for (const key in form) {
      const defaultValue = form[key].defaultValue;

      setForm(prevState => ({
        ...prevState,
        [key]: {
          ...prevState[key],
          value: defaultValue,
          defaultValue: defaultValue,
          valid: true,
          errorMessage: '',
          touched: false,
        },
      }));
  }
  }, [form]);

  return { 
    form,
    renderFormInputs,
    renderFormInput,
    registerFormInput,
    resetForm,
    isFormValid,
    setFormFieldValue,
    setDefaultValues
  };
}

export default useForm;
